import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Trash2, Edit3, Plus, Ban, CheckCircle2, X } from "lucide-react";

const TABS = ["news", "users", "community", "categories"];

export default function AdminDashboard() {
  const [tab, setTab] = useState("news");
  return (
    <div className="pt-28 pb-20 max-w-[1400px] mx-auto px-6 md:px-10" data-testid="admin-dashboard">
      <div className="flex items-center gap-3 mb-2">
        <span className="font-mono text-xs text-yellow-400 uppercase tracking-[0.3em]">/admin</span>
      </div>
      <h1 className="font-display font-bold text-5xl md:text-6xl text-white tracking-tighter">Control Room</h1>
      <p className="text-zinc-400 mt-2">Moderate the site and manage editorial content.</p>

      <div className="mt-10 flex border-b border-white/10">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`admin-tab-${t}`}
            className={`px-5 py-3 text-sm uppercase tracking-widest font-mono ${
              tab === t ? "text-yellow-400 border-b-2 border-yellow-400" : "text-zinc-400 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "news" && <NewsAdmin />}
        {tab === "users" && <UsersAdmin />}
        {tab === "community" && <CommunityAdmin />}
        {tab === "categories" && <CategoriesAdmin />}
      </div>
    </div>
  );
}

function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const blank = { title: "", excerpt: "", content: "", image_url: "", category_slug: "", featured: false, tags: [] };
  const [form, setForm] = useState(blank);

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/news?limit=100"), api.get("/categories")]);
    setItems(a.data); setCats(c.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
      if (editing) await api.put(`/news/${editing}`, payload);
      else await api.post("/news", payload);
      toast.success(editing ? "Article updated" : "Article created");
      setEditing(null); setForm(blank); load();
    } catch (e) { toast.error("Save failed"); }
  };

  const startEdit = (a) => {
    setEditing(a.id);
    setForm({ ...a, tags: (a.tags || []).join(", ") });
    window.scrollTo({ top: 200, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!confirm("Delete this article?")) return;
    await api.delete(`/news/${id}`); toast.success("Article deleted"); load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <form onSubmit={save} className="lg:col-span-5 bg-[#0a0a0b] border border-white/10 p-6 space-y-3" data-testid="admin-news-form">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-xl text-white">{editing ? "Edit article" : "New article"}</h3>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm(blank); }} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>}
        </div>
        <input data-testid="admin-news-title" required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-[#050505] border border-white/20 px-3 py-2 text-white" />
        <input data-testid="admin-news-image" placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full bg-[#050505] border border-white/20 px-3 py-2 text-white" />
        <select data-testid="admin-news-category" value={form.category_slug} onChange={(e) => setForm({ ...form, category_slug: e.target.value })} className="w-full bg-[#050505] border border-white/20 px-3 py-2 text-white">
          <option value="">— Select category —</option>
          {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <input data-testid="admin-news-tags" placeholder="Tags (comma-separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="w-full bg-[#050505] border border-white/20 px-3 py-2 text-white" />
        <textarea data-testid="admin-news-excerpt" placeholder="Excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full bg-[#050505] border border-white/20 px-3 py-2 text-white resize-none" />
        <textarea data-testid="admin-news-content" required placeholder="Content (use blank lines for paragraphs)" rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="w-full bg-[#050505] border border-white/20 px-3 py-2 text-white resize-none" />
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" data-testid="admin-news-featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured on homepage
        </label>
        <button data-testid="admin-news-save" type="submit" className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-5 py-2 hover:bg-yellow-500"><Plus className="inline w-4 h-4 mr-1" /> {editing ? "Update" : "Publish"}</button>
      </form>
      <div className="lg:col-span-7 space-y-3">
        {items.map((a) => (
          <div key={a.id} className="bg-[#0a0a0b] border border-white/10 p-4 flex items-center gap-4" data-testid={`admin-news-row-${a.id}`}>
            {a.image_url && <img src={a.image_url} alt="" className="w-16 h-16 object-cover" />}
            <div className="flex-1">
              <div className="font-display font-bold text-white">{a.title}</div>
              <div className="text-xs font-mono text-zinc-500 mt-1">{a.category_slug} · {a.featured ? "FEATURED" : ""} · {new Date(a.created_at).toLocaleDateString()}</div>
            </div>
            <button onClick={() => startEdit(a)} data-testid={`admin-news-edit-${a.id}`} className="text-zinc-400 hover:text-yellow-400"><Edit3 className="w-4 h-4" /></button>
            <button onClick={() => remove(a.id)} data-testid={`admin-news-delete-${a.id}`} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const load = async () => { const { data } = await api.get("/admin/users"); setUsers(data); };
  useEffect(() => { load(); }, []);
  const ban = async (u) => { await api.post(`/admin/users/${u.id}/${u.banned ? "unban" : "ban"}`); toast.success(u.banned ? "User unbanned" : "User banned"); load(); };
  const del = async (u) => { if (!confirm(`Delete ${u.email}?`)) return; await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); load(); };
  return (
    <div className="bg-[#0a0a0b] border border-white/10" data-testid="admin-users-table">
      <table className="w-full text-sm">
        <thead className="text-xs font-mono uppercase tracking-widest text-zinc-500">
          <tr className="border-b border-white/10"><th className="text-left p-4">Email</th><th className="text-left p-4">Name</th><th className="text-left p-4">Role</th><th className="text-left p-4">Status</th><th className="p-4"></th></tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-white/5" data-testid={`admin-user-row-${u.id}`}>
              <td className="p-4 text-white">{u.email}</td>
              <td className="p-4 text-zinc-300">{u.name}</td>
              <td className="p-4"><span className={`font-mono text-xs uppercase ${u.role === "admin" ? "text-yellow-400" : "text-zinc-400"}`}>{u.role}</span></td>
              <td className="p-4">{u.banned ? <span className="text-red-400 text-xs font-mono">BANNED</span> : <span className="text-emerald-400 text-xs font-mono">ACTIVE</span>}</td>
              <td className="p-4 flex justify-end gap-3">
                {u.role !== "admin" && (
                  <>
                    <button onClick={() => ban(u)} data-testid={`admin-user-ban-${u.id}`} className="text-zinc-400 hover:text-yellow-400">{u.banned ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}</button>
                    <button onClick={() => del(u)} data-testid={`admin-user-delete-${u.id}`} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CommunityAdmin() {
  const [posts, setPosts] = useState([]);
  const load = async () => { const { data } = await api.get("/community/posts?limit=100"); setPosts(data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!confirm("Delete post?")) return; await api.delete(`/community/posts/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="space-y-3" data-testid="admin-community-list">
      {posts.map((p) => (
        <div key={p.id} className="bg-[#0a0a0b] border border-white/10 p-4 flex items-center gap-4">
          <div className="flex-1">
            <div className="font-display font-bold text-white">{p.title}</div>
            <div className="text-xs font-mono text-zinc-500 mt-1">By {p.author_name} · {new Date(p.created_at).toLocaleString()} · ❤ {p.likes_count} · 💬 {p.comments_count}</div>
            <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{p.content}</p>
          </div>
          <button onClick={() => del(p.id)} data-testid={`admin-community-delete-${p.id}`} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

function CategoriesAdmin() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const load = async () => { const { data } = await api.get("/categories"); setCats(data); };
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/categories", { name, description: desc });
      toast.success("Category added");
      setName(""); setDesc(""); load();
    } catch (_) { toast.error("Could not add"); }
  };
  const del = async (id) => { if (!confirm("Delete category?")) return; await api.delete(`/categories/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <form onSubmit={add} className="lg:col-span-5 bg-[#0a0a0b] border border-white/10 p-6 space-y-3" data-testid="admin-cat-form">
        <h3 className="font-display font-bold text-xl text-white">New category</h3>
        <input data-testid="admin-cat-name" required placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-[#050505] border border-white/20 px-3 py-2 text-white" />
        <textarea data-testid="admin-cat-desc" placeholder="Description" rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} className="w-full bg-[#050505] border border-white/20 px-3 py-2 text-white resize-none" />
        <button data-testid="admin-cat-add" type="submit" className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-5 py-2 hover:bg-yellow-500">Add</button>
      </form>
      <div className="lg:col-span-7 space-y-3">
        {cats.map((c) => (
          <div key={c.id} className="bg-[#0a0a0b] border border-white/10 p-4 flex items-center gap-4">
            <div className="flex-1">
              <div className="font-display font-bold text-white">{c.name}</div>
              <div className="text-xs font-mono text-zinc-500">/{c.slug}</div>
              <p className="text-sm text-zinc-400 mt-1">{c.description}</p>
            </div>
            <button onClick={() => del(c.id)} data-testid={`admin-cat-delete-${c.id}`} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
